import { useCallback, useEffect, useRef } from 'react';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useOnForeground } from 'shared/src/hooks';
import type { MumulLiveProfileStatus } from 'shared/src/api';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { useMumulLiveProfileStatus } from '../../../queries/useMumulLiveProfileStatus';
import { useMumulLiveCurrentCampaign } from '../../../queries/useMumulLiveCurrentCampaign';
import { useGoodsInstanceId } from '../../../hooks/useGoodsInstanceId';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import {
  useElementViewportTracking,
  useIsSectionVisible,
} from '../../../store/sectionVisibility';
import { useOnPressMumulLiveProfile } from './useOnPressMumulLiveProfile';
import { useLogMumulLiveProfileBadge } from './useLogMumulLiveProfileBadge';
import { useLogCheckExp1789Target } from './useLogCheckExp1789Target';

// 프로필 행 대략 높이(px). 행 전체가 뷰포트에 들어왔을 때만 노출로 판정하기 위한 offset.
const VIEWPORT_OFFSET = 80;

type UseMumulLiveProfileResult = {
  /** 섹션(집계용 여백) 노출 여부. false 면 미노출(비실험/DEFAULT/리허설/미로드). */
  isVisible: boolean;
  /** 실험군(B) 여부. 대조군(A)은 집계용 여백만 렌더한다. */
  isExposed: boolean;
  status: MumulLiveProfileStatus | undefined;
  marketName: string;
  marketImageUrl: string;
  onPress: () => void;
  /** 프로필 행의 뷰포트 진입 감지를 위해 프로필 래퍼 View 에 연결할 ref. */
  trackingRef: ReturnType<typeof useElementViewportTracking>;
  /** 집계용 여백의 뷰포트 진입 감지를 위해 연결할 ref. */
  expTargetRef: ReturnType<typeof useElementViewportTracking>;
};

/**
 * EXP1789 상품상세 무물라이브 프로필(간판 영역) 오케스트레이션.
 * 실험군(B) 게이팅 → 상태 조회 → LIVE면 현재 캠페인(리허설 제외) → 노출 판정 + 노출 로깅 + 클릭 액션.
 */
export const useMumulLiveProfile = (): UseMumulLiveProfileResult => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();

  const profileSno = goodsDetail?.goods.market.mumul_live_profile_sno ?? null;
  const marketName = goodsBasic?.market.name ?? '';
  const marketImageUrl = goodsBasic?.market.image ?? '';

  // EXP1789 참여 대상(A/B)이면 조회한다. 대조군(A)도 동일 조건으로 노출 모수를 집계해야
  // 하므로 status 조회까지는 양쪽 모두 수행하고, 프로필 렌더만 실험군(B)으로 제한한다.
  // 단, 같은 간판 영역의 브랜드 정보(BrandInfo)가 우선이므로 brand 가 있으면
  // 무물라이브는 조회·노출 모두 생략한다. (1순위 브랜드 / 2순위 무물라이브)
  const isExpTarget = useExperimentGroup({
    sno: 'EXP1789',
    targetGroups: ['A', 'B'],
  });
  const isExposed = useExperimentGroup({ sno: 'EXP1789', targetGroups: ['B'] });
  const hasBrand = goodsBasic?.brand != null;
  const shouldExpose = isExpTarget && !hasBrand;

  const { data: statusData, refetch: refetchStatus } =
    useMumulLiveProfileStatus(profileSno, shouldExpose);

  // 스토리/라이브 네이티브 화면 다녀온 뒤 포그라운드 복귀 시 상태 갱신
  // (PDP 는 unmount 되지 않아 staleTime/refetchOnMount 로는 갱신되지 않는다)
  useOnForeground(
    useCallback(() => {
      refetchStatus();
    }, [refetchStatus]),
  );

  const status = statusData?.profile_status;
  const isOnair = status === 'LIVE';

  // LIVE 노출 시에만 현재 캠페인 조회 (campaign_sno / mode 확인용)
  const { data: campaignData, refetch: refetchCurrentCampaign } =
    useMumulLiveCurrentCampaign(profileSno, shouldExpose && isOnair);

  const onPress = useOnPressMumulLiveProfile({
    profileSno,
    profileName: marketName,
    status,
    refetchCurrentCampaign,
    refetchStatus,
  });

  // 노출 판정:
  // - 미실험 / profileSno 없음 / DEFAULT → 미노출
  // - LIVE: 현재 캠페인이 있고 mode === 'LIVE' (리허설/종료 제외) 일 때만 노출
  // - 스토리(HAS_UNREAD / ALL_READ): 그대로 노출
  const campaign = campaignData?.campaign ?? null;
  const campaignSno = campaign?.sno ?? null;
  const isVisible =
    shouldExpose &&
    profileSno != null &&
    status != null &&
    status !== 'DEFAULT' &&
    (isOnair ? campaign != null && campaign.mode === 'LIVE' : true);

  // 실제 뷰포트 진입 추적.
  // 간판 영역은 보통 첫 화면 밖(상품 이미지 아래)이라 status API 응답만으로 로깅하면
  // 화면에 보이지 않았는데도 노출이 집계된다(codiGoods/midRelatedGoods 와 동일 이슈).
  // 행이 500ms 이상 뷰포트에 머문 뒤에만 noted 되도록 게이팅한다.
  const viewportKey = useGoodsInstanceId('mumul_live_profile');
  const trackingRef = useElementViewportTracking(viewportKey, VIEWPORT_OFFSET);
  const isInViewport = useIsSectionVisible(viewportKey);

  // 집계용 여백은 1px 이라 offset 없이 그대로 판정한다.
  const expTargetKey = useGoodsInstanceId('mumul_live_exp_target');
  const expTargetRef = useElementViewportTracking(expTargetKey);
  const isExpTargetInViewport = useIsSectionVisible(expTargetKey);

  // DISPLAY_LIVE_PROFILE_BADGE: 노출 확정 + 뷰포트 진입 시 발화 (LIVE 는 campaign_sno 확보 후 노출되므로 함께 전달)
  // status 별로 1회만 집계한다. PDP 는 unmount 되지 않고 포그라운드 복귀마다 status 를
  // 재조회하므로(예: HAS_UNREAD_STORY → ALL_READ_STORY), 가드가 없으면 같은 노출이
  // 중복 집계되어 실험 지표가 부풀려진다.
  const { logDisplayLiveProfileBadge } = useLogMumulLiveProfileBadge();
  const loggedStatusKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (
      !isVisible ||
      !isExposed ||
      !isInViewport ||
      profileSno == null ||
      status == null
    )
      return;
    const loggedKey = `${profileSno}:${status}`;
    if (loggedStatusKeys.current.has(loggedKey)) return;
    loggedStatusKeys.current.add(loggedKey);
    logDisplayLiveProfileBadge({
      profileSno,
      profileName: marketName,
      status,
      campaignSno: isOnair ? campaignSno : null,
    });
  }, [
    isVisible,
    isExposed,
    isInViewport,
    profileSno,
    status,
    marketName,
    isOnair,
    campaignSno,
    logDisplayLiveProfileBadge,
  ]);

  // CHECK_EXP_1789_TARGET: 대조군(A)·실험군(B) 공통 여백 노출 시 상품당 1회.
  // status 가 바뀌어도(스토리 읽음 등) 재발화하지 않도록 profileSno 기준으로 latch 한다.
  const goodsSno = useGoodsSno();
  const logCheckExp1789Target = useLogCheckExp1789Target();
  const loggedExpTargetProfileSno = useRef<number | null>(null);
  useEffect(() => {
    if (
      !isVisible ||
      !isExpTargetInViewport ||
      profileSno == null ||
      status == null
    )
      return;
    if (loggedExpTargetProfileSno.current === profileSno) return;
    loggedExpTargetProfileSno.current = profileSno;
    logCheckExp1789Target({ goodsSno, profileSno, status });
  }, [
    isVisible,
    isExpTargetInViewport,
    goodsSno,
    profileSno,
    status,
    logCheckExp1789Target,
  ]);

  return {
    isVisible,
    isExposed,
    status,
    marketName,
    marketImageUrl,
    onPress,
    trackingRef,
    expTargetRef,
  };
};
